import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Reveal, Hairline } from "@/components/primitives";

export default function Foundation() {
  useEffect(() => {
    const upsertLink = (rel, href, sizes) => {
      let node = document.head.querySelector(`link[rel="${rel}"]`);
      const snapshot = node
        ? { node, href: node.getAttribute("href"), sizes: node.getAttribute("sizes"), created: false }
        : { node: document.createElement("link"), href: null, sizes: null, created: true };

      if (snapshot.created) {
        snapshot.node.setAttribute("rel", rel);
        document.head.appendChild(snapshot.node);
      }
      snapshot.node.setAttribute("href", href);
      if (sizes) snapshot.node.setAttribute("sizes", sizes);
      else snapshot.node.removeAttribute("sizes");
      return snapshot;
    };

    const icon = upsertLink("icon", "/brand/foundation-20260929-32.png", "32x32");
    const apple = upsertLink("apple-touch-icon", "/brand/foundation-20260929-180.png", "180x180");
    const manifest = upsertLink("manifest", "/brand/foundation-20260929.webmanifest");

    const appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const previousAppTitle = appTitle?.getAttribute("content");
    if (appTitle) appTitle.setAttribute("content", "Jennae Michelle Foundation");

    const theme = document.querySelector('meta[name="theme-color"]');
    const previousTheme = theme?.getAttribute("content");
    if (theme) theme.setAttribute("content", "#f7f3e8");

    return () => {
      for (const item of [icon, apple, manifest]) {
        if (item.created) item.node.remove();
        else {
          if (item.href == null) item.node.removeAttribute("href");
          else item.node.setAttribute("href", item.href);
          if (item.sizes == null) item.node.removeAttribute("sizes");
          else item.node.setAttribute("sizes", item.sizes);
        }
      }
      if (appTitle && previousAppTitle != null) appTitle.setAttribute("content", previousAppTitle);
      if (theme && previousTheme != null) theme.setAttribute("content", previousTheme);
    };
  }, []);

  return (
    <div className="jmh-page jmh-foundation-page bg-[var(--stone)] text-[var(--ink)]" data-testid="foundation-page">
      <Seo title="The Jennae Michelle Foundation" description="Be the light that shows others what they are capable of. The philanthropic arm of the Jennae Michelle Holdings ecosystem, created to help people recognize and develop their own capabilities." />
      <h1 className="sr-only">Jennae Michelle Foundation</h1>

      <section className="text-center jmh-section jmh-page-opening">
        <Reveal>
          <img src="/brand/foundation-official.png" alt="Jennae Michelle Foundation" className="mx-auto w-full jmh-page-brand" />
        </Reveal>
      </section>

      <section className="text-center jmh-section jmh-section-compact">
        <Reveal>
          <h2 className="font-display uppercase tracking-wide2 text-xl md:text-3xl leading-snug text-[var(--ink)]">Be the light that shows others<br />what they are capable of.</h2>
        </Reveal>
      </section>

      <section className="jmh-section jmh-section-compact">
        <div className="mx-auto jmh-content-shell"><Reveal><Hairline className="opacity-70" /></Reveal></div>
      </section>

      <section className="jmh-section">
        <div className="mx-auto text-center space-y-6 jmh-content-shell">
          <Reveal><h2 className="font-serif2 italic text-2xl md:text-3xl leading-snug text-[var(--ink)]">Light does not create someone’s capability. It helps them recognize what was already there.</h2></Reveal>
          <Reveal delay={0.08}><p className="mt-6 text-lg md:text-xl leading-relaxed text-[var(--ink-muted)]">The Jennae Michelle Foundation begins with a conviction shaped by my own experience: meaningful support respects the capability of the person receiving it. It should expand access to experiences, encourage self-recognition, and create room for growth while preserving dignity and individual agency. What a person discovers about their own abilities should remain theirs to carry forward.</p></Reveal>
          <Reveal delay={0.14}><p className="text-lg md:text-xl leading-relaxed text-[var(--ink)]">The most enduring contribution we make to another person may be the perspective, encouragement, or opportunity that enables them to recognize their capabilities and determine their own direction. This Foundation was created to pursue that form of support.</p></Reveal>
        </div>
      </section>

      <section className="jmh-section jmh-section-compact">
        <div className="mx-auto text-center space-y-8 jmh-content-shell">
          <Reveal><p className="italic text-[var(--ink-muted)]">The Foundation will announce its programs and giving initiatives as their scope, resources, and implementation are established.</p></Reveal>
          <Reveal delay={0.08}>
            <Link to="/contact" data-testid="foundation-contact" className="jmh-foundation-cta inline-block px-9 py-3.5 bg-[var(--ink)] text-[var(--stone)] font-display uppercase tracking-wide2 text-xs border border-[var(--ink)] hover:bg-transparent hover:text-[var(--ink)] transition-colors">
              Contact the Foundation
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
